import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './ShippingServiceForm.css';
import '../../../shared/StyledInput.css';
import RequiredMark from '../../../shared/RequiredMark';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import ToggleSwitch from '../../../shared/ToggleSwitch';
import { isAustraliaPost, auspostServiceName } from '../../warehouse/packageShared';

function ShippingServiceForm({ initial, onSave, onCancel, loading }) {
  const [description, setDescription] = useState(initial?.description || '');
  const [supplier_id, setSupplierId] = useState(initial?.supplier_id ?? '');
  const [disclaimer, setDisclaimer] = useState(initial?.disclaimer || '');
  const [showDisclaimer, setShowDisclaimer] = useState(initial?.show_disclaimer ?? true);
  const [costConfirmedLater, setCostConfirmedLater] = useState(initial?.cost_confirmed_later ?? false);
  const [pendingCostDisclaimer, setPendingCostDisclaimer] = useState(initial?.pending_cost_disclaimer || '');
  const [carrierServiceCode, setCarrierServiceCode] = useState(initial?.carrier_service_code || '');
  const [suppliers, setSuppliers] = useState([]);
  // Australia Post's own services (issue #316), once the Carrier is
  // Australia Post: { services } or { message } saying why there are none.
  const [apServices, setApServices] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get('/api/supplier-entities', { params: { status: 'active', include_no_carrier: true } })
      .then((res) => setSuppliers(res.data.data || []))
      .catch(() => setSuppliers([]));
  }, []);

  const supplier = suppliers.find((s) => String(s.id) === String(supplier_id));
  const isAusPost = !!supplier && isAustraliaPost(supplier.name);

  useEffect(() => {
    if (!isAusPost || apServices) return;
    axios.get('/api/australia-post/services')
      .then((res) => {
        const result = res.data.result || {};
        setApServices(result.ok ? { services: result.services } : { message: result.message });
      })
      .catch((err) => setApServices({
        message: err.response?.status === 403 ? 'Only an Admin can change which Australia Post service this is priced as.' : (err.response?.data?.message || 'Couldn’t load Australia Post’s services'),
        locked: err.response?.status === 403,
      }));
  }, [isAusPost, apServices]);

  const supplierOptions = suppliers.filter(
    (s) => s.is_active || String(s.id) === String(supplier_id)
  );

  const validate = () => {
    if (!description.trim()) return 'Description is required.';
    return '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return setError(err);
    setError('');
    onSave({
      description: description.trim(),
      supplier_id: supplier_id === '' ? null : Number(supplier_id),
      disclaimer: disclaimer.trim(),
      show_disclaimer: showDisclaimer,
      cost_confirmed_later: costConfirmedLater,
      pending_cost_disclaimer: pendingCostDisclaimer.trim(),
      // Only an Admin can change it; anyone else's save leaves it alone.
      ...(isAusPost && apServices?.services ? { carrier_service_code: carrierServiceCode || null } : {}),
    });
  };

  return (
    <form className="shipping-service-form" onSubmit={handleSubmit} style={{ minWidth: 320 }}>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <div>
          <label>Description<RequiredMark /></label><br />
          <input
            className="styled-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={150}
            placeholder="e.g., MyPost Business"
            style={{ width: 260 }}
          />
        </div>
        <div>
          <label title="The Carrier that delivers this Shipping Service. No Carrier is for a Service with no real carrier, e.g. Customer Pickup, and offers it under every Carrier.">Carrier</label><br />
          <select
            className="styled-input"
            value={supplier_id}
            onChange={(e) => setSupplierId(e.target.value)}
            style={{ width: 200 }}
          >
            <option value="">(none)</option>
            {supplierOptions.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
      </div>

      {isAusPost && (
        <div style={{ marginTop: 14 }}>
          <label title="The Australia Post service this Shipping Service is priced as at Ship. Leave it blank and its shipments ship unpriced.">Australia Post Service</label><br />
          {apServices?.services ? (
            <select
              className="styled-input"
              value={carrierServiceCode}
              onChange={(e) => setCarrierServiceCode(e.target.value)}
              style={{ width: 260 }}
            >
              <option value="">(not linked)</option>
              {carrierServiceCode && !apServices.services.some((s) => s.code === carrierServiceCode) && <option value={carrierServiceCode}>{auspostServiceName(carrierServiceCode)}</option>}
              {apServices.services.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
            </select>
          ) : (
            <div style={{ fontSize: 13, color: '#555' }}>
              {carrierServiceCode ? `Linked to ${auspostServiceName(carrierServiceCode)}` : 'Not linked'}
              {apServices?.message && <div style={{ fontSize: 12, color: apServices.locked ? '#888' : '#b91c1c', marginTop: 4 }}>{apServices.message}</div>}
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        <label title="Reinforces a clause of the Customer's already-signed account contract at the point of choice (e.g. loss in transit is the Customer's responsibility unless insured). Not yet shown anywhere else.">Disclaimer</label><br />
        <textarea
          className="styled-input"
          value={disclaimer}
          onChange={(e) => setDisclaimer(e.target.value)}
          rows={3}
          style={{ width: '100%', maxWidth: 480, resize: 'vertical' }}
          placeholder="Optional wording reinforcing the account contract, e.g. loss in transit is the Customer's responsibility unless insured"
        />
        <div style={{ marginTop: 8 }}>
          <ToggleSwitch checked={showDisclaimer} onChange={setShowDisclaimer} label="Show Disclaimer on Quote" />
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <ToggleSwitch
          checked={costConfirmedLater}
          onChange={setCostConfirmedLater}
          label="Cost confirmed later"
        />
        <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
          For a Shipping Service like Courier, where the cost is only known once weight/dimensions are confirmed. Leave Shipping Cost blank on the Quote or Sales Order until then, and the Pending Cost Message prints in its place.
        </div>
        {costConfirmedLater && (
          <div style={{ marginTop: 8 }}>
            <label title="Shown on the Quote and Sales Order in place of a dollar figure whenever this Shipping Service is selected and Shipping Cost is blank.">Pending Cost Message</label><br />
            <textarea
              className="styled-input"
              value={pendingCostDisclaimer}
              onChange={(e) => setPendingCostDisclaimer(e.target.value)}
              rows={3}
              style={{ width: '100%', maxWidth: 480, resize: 'vertical' }}
              placeholder="e.g. Shipping cost to be confirmed based on final weight/dimensions and added to this order. By proceeding, you accept this additional cost."
            />
          </div>
        )}
      </div>

      {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}

      <div style={{ marginTop: 12 }}>
        <SaveButton loading={loading} />
        <CancelButton onClick={onCancel} disabled={loading} style={{ marginLeft: 8 }} />
      </div>
    </form>
  );
}

export default ShippingServiceForm;
